/** A post's comment counts as the API returns them. */
export type BreakdownCounts = {
    collected: number
    safe: number
    suspect: number
    severe: number
}

export type BreakdownSegment = {
    id: 'safe' | 'suspect' | 'severe' | 'unmoderated'
    label: string
    count: number
    /** Share of the collected total, 0–100. */
    percent: number
}

/**
 * The Safe / Suspect / Severe split of a post's collected comments, as widths
 * for a stacked bar. Pure so the arithmetic is testable without rendering.
 *
 * Comments we hold but have not moderated are a real bucket — collection runs
 * ahead of classification, and some accounts have classification switched off —
 * so the bar shows them rather than letting the three coloured bands quietly
 * fall short of the total. The SQL counts distinct comments per bucket, but a
 * comment whose verdict changed sits in two buckets, so the moderated sum can
 * exceed `collected`: the unmoderated width is clamped at zero and every width
 * is taken over whichever total is larger, so the bar neither goes negative
 * nor overflows.
 */
export function breakdownSegments(counts: BreakdownCounts): BreakdownSegment[] {
    const { collected, safe, suspect, severe } = counts
    const unmoderated = Math.max(0, collected - safe - suspect - severe)
    const denominator = Math.max(collected, safe + suspect + severe)
    const percent = (count: number) => (denominator === 0 ? 0 : (count / denominator) * 100)

    return [
        { id: 'severe', label: 'Severe', count: severe, percent: percent(severe) },
        { id: 'suspect', label: 'Suspect', count: suspect, percent: percent(suspect) },
        { id: 'safe', label: 'Safe', count: safe, percent: percent(safe) },
        {
            id: 'unmoderated',
            label: 'Unmoderated',
            count: unmoderated,
            percent: percent(unmoderated)
        }
    ]
}
